import { getMessaging, getToken } from "firebase/messaging";
import { unregisterFcmServiceWorker } from "../../AuthProvider";
import { useFcmRegistration } from "../../queries/fcm/use-fcm-registration";
import { firebaseApp } from "../../services/firebase/firebase";
import { getTenantFromHostname } from "../../utils/get-tenant-from-hostname";
import { useSnackbar } from "../useSnackbar";

const FIREBASE_MESSAGING_SW_PATH = "/firebase-messaging-sw.js";

/**
 * Resolve once the service worker tied to this registration is activated.
 * A freshly registered worker is "installing"/"waiting" for a moment; calling
 * getToken before it activates is a common cause of token retrieval failing.
 */
const waitForServiceWorkerActivation = (
  registration: ServiceWorkerRegistration
): Promise<void> =>
  new Promise((resolve) => {
    if (registration.active) {
      resolve();
      return;
    }
    const worker = registration.installing || registration.waiting;
    if (!worker) {
      resolve();
      return;
    }
    worker.addEventListener("statechange", () => {
      if (worker.state === "activated") resolve();
    });
  });

/**
 * Encapsulates the full FCM device-registration flow used after login:
 *  1. ensure notification permission (prompt when "default", guide when "denied")
 *  2. tear down any stale messaging service worker and register a fresh one
 *  3. wait for the worker to activate, then retrieve the FCM token
 *  4. register the token with the backend
 *
 * Returns `registerFcmToken`, which is safe to call on every login – it never
 * throws, so it can never break the login flow.
 *
 * @author [Rajkumar Rathod]
 */
export const useFcmTokenRegistration = () => {
  const showSnackbar = useSnackbar();
  const { mutateAsync: fcmRegistration } = useFcmRegistration();

  const registerFcmToken = async (): Promise<void> => {
    try {
      /** bail out gracefully on browsers without notification/SW support */
      if (!("Notification" in window) || !("serviceWorker" in navigator)) {
        return;
      }

      /**
       * Ensure we have notification permission BEFORE requesting a token.
       * getToken() does not prompt on its own – it only succeeds when
       * permission is already "granted". So ask for it when it is still
       * "default", and stop if the user has explicitly denied it.
       */
      let permission = Notification.permission;
      if (permission === "default") {
        permission = await Notification.requestPermission();
      }

      /**
       * If the user has manually blocked notifications, the browser will
       * NOT show the native prompt again (requestPermission resolves to
       * "denied" without any popup). We cannot programmatically re-enable
       * it – only the user can, from their browser's site settings. So
       * guide them there instead of failing silently.
       */
      if (permission === "denied") {
        showSnackbar({
          message:
            "Notifications are blocked. Please enable them for this site in your browser settings to receive alerts.",
          type: "warning",
        });
        return;
      }

      if (permission !== "granted") {
        return;
      }

      /**
       * Unregister any stale messaging worker first. If a worker is already
       * active at this scope, register() just hands back the old registration
       * (and its old push subscription), which can make getToken fail – so the
       * register API never gets called. Tearing it down guarantees a clean,
       * freshly-activated worker on every login.
       */
      await unregisterFcmServiceWorker();

      const swRegistration = await navigator.serviceWorker.register(
        FIREBASE_MESSAGING_SW_PATH
      );

      /** wait until the fresh worker is actually activated before getToken */
      await waitForServiceWorkerActivation(swRegistration);

      const messaging = getMessaging(firebaseApp);

      /** get fcm token from firebase */
      const fcmToken = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: swRegistration,
      });

      if (!fcmToken) {
        return;
      }

      /** register the token with the backend */
      await fcmRegistration({
        registrationId: fcmToken.toString(),
        xTENANTID: getTenantFromHostname(),
      });
    } catch (e) {
      console.error("Error during FCM token registration:", e);
    }
  };

  return { registerFcmToken };
};
